<template>
  <main class="site-page relative min-h-screen overflow-hidden bg-[var(--page)] text-[var(--text)] antialiased" :style="{
    '--pointer-x': `${pointer.x}%`,
    '--pointer-y': `${pointer.y}%`
  }" @pointermove="handlePointerMove">
    <!-- AMBIENTE -->
    <div class="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
      <div class="ambient-grid absolute inset-0"></div>
      <div class="ambient-noise absolute inset-0"></div>
      <div class="pointer-glow absolute inset-0"></div>
      <div class="ambient-orb ambient-orb-one"></div>
      <div class="ambient-orb ambient-orb-two"></div>
      <div class="ambient-orb ambient-orb-three"></div>
    </div>

    <!-- VOLTAR AO TOPO -->
    <transition name="scroll-top">
      <button v-show="showScrollTop" type="button"
        class="fixed bottom-5 right-5 z-50 grid h-11 w-11 place-items-center rounded-full border border-[var(--border)] bg-[var(--glass-strong)] text-[var(--text-strong)] shadow-[var(--shadow-soft)] backdrop-blur-2xl transition duration-300 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--focus)]"
        aria-label="Voltar ao topo" title="Voltar ao topo" @click="scrollToTop">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
          class="h-5 w-5" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 15l6-6 6 6" />
        </svg>
      </button>
    </transition>

    <!-- HERO -->
    <section class="relative z-10">
      <div class="mx-auto max-w-7xl px-5 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-28">
        <div class="mx-auto max-w-5xl text-center" data-reveal>
          <div
            class="inline-flex items-center gap-2 rounded-full border border-[var(--border)] bg-[var(--glass)] px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--muted)] shadow-[var(--shadow-soft)] backdrop-blur-2xl">
            <span class="relative flex h-2 w-2">
              <span
                class="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-40"></span>
              <span class="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
            </span>
            Portfólio
            <span class="hidden text-[var(--muted-2)] sm:inline">/ Selected work</span>
          </div>

          <h1
            class="mx-auto mt-7 max-w-5xl text-5xl font-semibold tracking-[-0.065em] text-[var(--text-strong)] sm:text-6xl lg:text-[5.45rem] lg:leading-[0.98]">
            Projetos reais.
            <span class="hero-gradient block">Construídos para operação real.</span>
          </h1>

          <p class="mx-auto mt-7 max-w-3xl text-lg leading-8 text-[var(--muted)] sm:text-xl">
            Produtos, sistemas, integrações e experiências digitais onde design,
            engenharia e contexto trabalham juntos.
          </p>

          <div class="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a :href="whatsHref" target="_blank" rel="noopener"
              class="primary-button group inline-flex h-12 items-center justify-center gap-2 rounded-full px-7 text-sm font-semibold focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--focus)]">
              Criar algo nessa linha
              <span class="transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true">→</span>
            </a>

            <router-link to="/solutions"
              class="secondary-button inline-flex h-12 items-center justify-center rounded-full px-7 text-sm font-semibold">
              Ver soluções
            </router-link>
          </div>
        </div>

        <!-- SELECTED WORK STAGE -->
        <div class="relative mx-auto mt-16 max-w-6xl" data-reveal>
          <div class="hero-halo absolute inset-x-16 top-14 h-44 rounded-full blur-3xl"></div>

          <div class="device-frame relative rounded-[2.3rem] p-2.5 sm:rounded-[2.8rem] sm:p-3">
            <div
              class="overflow-hidden rounded-[1.8rem] border border-white/10 bg-[#08090b] text-white sm:rounded-[2.35rem]">
              <div class="flex items-center justify-between border-b border-white/[0.07] px-5 py-4 sm:px-6">
                <div class="flex gap-2">
                  <span class="h-2.5 w-2.5 rounded-full bg-white/20"></span>
                  <span class="h-2.5 w-2.5 rounded-full bg-white/20"></span>
                  <span class="h-2.5 w-2.5 rounded-full bg-white/20"></span>
                </div>

                <div
                  class="hidden rounded-full bg-white/[0.06] px-4 py-2 text-[10px] font-semibold text-zinc-400 ring-1 ring-white/10 sm:block">
                  Grivaro / selected-work
                </div>

                <span class="rounded-full bg-white/[0.07] px-3 py-1.5 text-[10px] font-semibold text-zinc-300">
                  {{ projects.length }} projects
                </span>
              </div>

              <div class="grid gap-5 p-5 sm:p-7 lg:grid-cols-[1.1fr_.9fr] lg:p-8">
                <!-- featured -->
                <article class="featured-panel group relative overflow-hidden rounded-[1.7rem]">
                  <img :src="featuredProjects[0].image" :alt="featuredProjects[0].title"
                    class="absolute inset-0 h-full w-full object-cover transition duration-1000 group-hover:scale-[1.035]" />

                  <div class="featured-overlay absolute inset-0"></div>

                  <div class="relative flex min-h-[28rem] flex-col justify-between p-6 sm:p-7">
                    <div class="flex items-center justify-between gap-3">
                      <span
                        class="rounded-full bg-black/30 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-white ring-1 ring-white/15 backdrop-blur-xl">
                        {{ featuredProjects[0].categoryLabel }}
                      </span>

                      <span class="rounded-full bg-white/90 px-3 py-1.5 text-[10px] font-semibold text-black">
                        {{ featuredProjects[0].status }}
                      </span>
                    </div>

                    <div>
                      <p class="text-xs font-semibold uppercase tracking-[0.16em] text-white/55">
                        Featured project
                      </p>

                      <h2 class="mt-3 text-4xl font-semibold tracking-[-0.05em] sm:text-5xl">
                        {{ featuredProjects[0].title }}
                      </h2>

                      <p class="mt-4 max-w-xl text-sm leading-7 text-white/70">
                        {{ featuredProjects[0].description }}
                      </p>

                      <div class="mt-5 flex flex-wrap gap-2">
                        <span v-for="tag in featuredProjects[0].tags" :key="tag"
                          class="rounded-full bg-black/25 px-3 py-1.5 text-[10px] font-semibold text-white/75 ring-1 ring-white/10 backdrop-blur-xl">
                          {{ tag }}
                        </span>
                      </div>
                    </div>
                  </div>
                </article>

                <!-- right rail -->
                <div class="grid gap-4">
                  <article v-for="project in featuredProjects.slice(1)" :key="project.id"
                    class="dashboard-card group grid overflow-hidden rounded-[1.6rem] sm:grid-cols-[9rem_1fr]">
                    <div class="relative min-h-[11rem] overflow-hidden">
                      <img :src="project.image" :alt="project.title"
                        class="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105" />
                      <div class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    </div>

                    <div class="flex flex-col justify-between p-5">
                      <div>
                        <div class="flex items-center justify-between gap-3">
                          <span class="text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
                            {{ project.categoryLabel }}
                          </span>

                          <span class="h-2 w-2 rounded-full bg-emerald-400"></span>
                        </div>

                        <h3 class="mt-3 text-xl font-semibold tracking-[-0.03em]">
                          {{ project.title }}
                        </h3>

                        <p class="mt-2 line-clamp-2 text-xs leading-5 text-zinc-500">
                          {{ project.description }}
                        </p>
                      </div>

                      <div class="mt-4 flex flex-wrap gap-1.5">
                        <span v-for="tag in project.tags.slice(0, 3)" :key="tag"
                          class="rounded-full bg-white/[0.05] px-2.5 py-1 text-[9px] font-semibold text-zinc-500 ring-1 ring-white/[0.07]">
                          {{ tag }}
                        </span>
                      </div>
                    </div>
                  </article>
                </div>
              </div>

              <div class="grid gap-3 border-t border-white/[0.07] p-5 sm:grid-cols-3 sm:p-6">
                <article v-for="metric in metrics" :key="metric.label" class="dashboard-card rounded-[1.3rem] p-4">
                  <p class="text-2xl font-semibold tracking-[-0.04em]">{{ metric.value }}</p>
                  <p class="mt-1.5 text-xs text-zinc-500">{{ metric.label }}</p>
                </article>
              </div>
            </div>
          </div>

          <!-- TECH + SEARCH DOCK -->
          <div
            class="portfolio-dock relative z-20 mx-auto mt-6 w-[95%] rounded-[1.6rem] border border-[var(--border)] bg-[var(--glass-strong)] p-3 shadow-[var(--shadow-large)] backdrop-blur-2xl sm:w-[91%]">
            <div
              class="tech-rail overflow-hidden rounded-full border border-[var(--border)] bg-[var(--surface)] py-2.5">
              <div class="tech-track flex min-w-max items-center gap-2 px-3">
                <template v-for="loop in 2" :key="loop">
                  <span v-for="tech in techBadges" :key="`${loop}-${tech}`"
                    class="rounded-full bg-[var(--chip)] px-3 py-1.5 text-[10px] font-semibold text-[var(--muted)] ring-1 ring-[var(--border)]">
                    {{ tech }}
                  </span>
                </template>
              </div>
            </div>

            <div class="mt-3 grid gap-3 lg:grid-cols-[1fr_auto]">
              <label class="relative block">
                <span class="sr-only">Buscar projeto</span>

                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  stroke-width="1.8"
                  class="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--muted-2)]"
                  aria-hidden="true">
                  <circle cx="11" cy="11" r="7"></circle>
                  <path d="m20 20-3.5-3.5"></path>
                </svg>

                <input v-model="q" type="search" placeholder="Buscar por projeto, tecnologia ou tipo"
                  class="h-12 w-full rounded-full border border-[var(--border)] bg-[var(--surface)] pl-11 pr-5 text-sm font-medium text-[var(--text-strong)] outline-none transition placeholder:text-[var(--muted-2)] focus:border-[var(--text-strong)]" />
              </label>

              <div class="flex flex-wrap gap-2">
                <button v-for="category in categories" :key="category.value" type="button"
                  @click="setCategory(category.value)" :aria-pressed="activeCategory === category.value" :class="[
                    'h-12 rounded-full px-4 text-xs font-semibold transition duration-300 sm:px-5',
                    activeCategory === category.value
                      ? 'bg-[var(--text-strong)] text-[var(--inverse)]'
                      : 'border border-[var(--border)] bg-[var(--surface)] text-[var(--muted)] hover:-translate-y-0.5 hover:text-[var(--text-strong)]'
                  ]">
                  {{ category.label }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CONECTOR -->
    <div class="relative z-10 mx-auto mt-10 h-20 max-w-7xl px-5 sm:px-6 lg:px-8" aria-hidden="true">
      <div class="flow-line"></div>
      <div class="flow-node"></div>
    </div>

    <!-- PROJECTS -->
    <section class="relative z-10 py-16 sm:py-20">
      <div class="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div class="grid gap-10 lg:grid-cols-12 lg:items-end" data-reveal>
          <div class="lg:col-span-7">
            <p class="eyebrow">Projetos</p>

            <h2 class="section-title mt-4">
              Diferentes problemas.
              <span class="text-[var(--muted-2)]">A mesma obsessão por clareza.</span>
            </h2>
          </div>

          <div class="lg:col-span-5">
            <p class="text-lg leading-8 text-[var(--muted)]">
              De mobilidade pública e finanças a IA, integrações, desktop e presença digital.
            </p>

            <p class="mt-4 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--muted-2)]" aria-live="polite">
              {{ filteredProjects.length }}
              {{ filteredProjects.length === 1 ? 'projeto encontrado' : 'projetos encontrados' }}
            </p>
          </div>
        </div>

        <transition-group v-if="filteredProjects.length" name="catalog" tag="div"
          class="mt-14 grid gap-5 md:grid-cols-2 xl:grid-cols-12 xl:items-start">
          <article v-for="(project, index) in filteredProjects" :key="project.id" :class="[
            'project-card group overflow-hidden rounded-[2rem] border border-[var(--border)] bg-[var(--card)] transition duration-500',
            projectSpan(index)
          ]">
            <div :class="[
              'relative overflow-hidden bg-[var(--chip)]',
              projectImageClass(index)
            ]">
              <img :src="project.image" :alt="project.title"
                class="h-full w-full object-cover transition duration-1000 ease-out group-hover:scale-[1.04]"
                loading="lazy" />

              <div class="project-overlay absolute inset-0"></div>

              <div class="absolute left-4 top-4 flex flex-wrap gap-2">
                <span
                  class="rounded-full bg-black/30 px-3 py-1.5 text-[9px] font-semibold uppercase tracking-[0.11em] text-white ring-1 ring-white/15 backdrop-blur-xl">
                  {{ project.categoryLabel }}
                </span>

                <span :class="project.status === 'Online' ? 'bg-white text-black' : 'bg-black/35 text-white'"
                  class="rounded-full px-3 py-1.5 text-[9px] font-semibold ring-1 ring-white/15 backdrop-blur-xl">
                  {{ project.status }}
                </span>
              </div>

              <span
                class="absolute bottom-4 right-4 grid h-10 w-10 place-items-center rounded-full bg-white/90 text-sm font-semibold text-black opacity-0 shadow-lg transition duration-500 group-hover:opacity-100"
                aria-hidden="true">
                ↗
              </span>
            </div>

            <div class="p-6 sm:p-7">
              <h3 class="text-2xl font-semibold tracking-[-0.035em] text-[var(--text-strong)]">
                {{ project.title }}
              </h3>

              <p class="mt-3 text-sm leading-7 text-[var(--muted)]">
                {{ project.description }}
              </p>

              <div class="mt-6 flex flex-wrap gap-2">
                <span v-for="tag in project.tags" :key="tag"
                  class="rounded-full border border-[var(--border)] bg-[var(--surface)] px-3 py-1.5 text-[10px] font-semibold text-[var(--muted)]">
                  {{ tag }}
                </span>
              </div>

              <div class="mt-7 flex items-center justify-between gap-4 border-t border-[var(--border)] pt-5">
                <a v-if="project.url" :href="project.url" target="_blank" rel="noopener"
                  class="group/link inline-flex items-center gap-2 text-xs font-semibold text-[var(--text-strong)]">
                  Acessar projeto
                  <span class="transition-transform duration-300 group-hover/link:translate-x-0.5"
                    aria-hidden="true">→</span>
                </a>

                <span v-else class="inline-flex items-center gap-2 text-xs font-semibold text-[var(--muted-2)]">
                  <span class="h-1.5 w-1.5 rounded-full bg-[var(--muted-2)]"></span>
                  Projeto privado
                </span>

                <span class="text-[10px] font-semibold uppercase tracking-[0.13em] text-[var(--muted-2)]">
                  #{{ String(project.id).padStart(2, '0') }}
                </span>
              </div>
            </div>
          </article>
        </transition-group>

        <div v-else
          class="mt-14 rounded-[2rem] border border-[var(--border)] bg-[var(--glass)] p-10 text-center shadow-[var(--shadow-soft)] backdrop-blur-2xl">
          <p class="text-lg font-semibold text-[var(--text-strong)]">
            Nenhum projeto encontrado.
          </p>

          <p class="mt-2 text-sm text-[var(--muted)]">
            Tente outro termo ou volte para todos os projetos.
          </p>

          <button type="button" @click="clearFilters"
            class="secondary-button mt-6 inline-flex h-10 items-center justify-center rounded-full px-5 text-xs font-semibold">
            Limpar filtros
          </button>
        </div>
      </div>
    </section>

    <!-- CURVA -->
    <div class="relative z-10 mx-auto max-w-7xl px-5 sm:px-6 lg:px-8" aria-hidden="true">
      <div class="curve-transition">
        <span class="curve-dot"></span>
      </div>
    </div>

    <!-- PRINCÍPIOS -->
    <section class="relative z-10 py-14 sm:py-20">
      <div class="mx-auto max-w-[88rem] px-3 sm:px-5 lg:px-7">
        <div
          class="dark-panel relative overflow-hidden rounded-[2.7rem] px-5 py-16 text-white shadow-[var(--shadow-large)] sm:px-8 sm:py-20 lg:px-12 xl:px-16">
          <div class="dark-panel-grid pointer-events-none absolute inset-0" aria-hidden="true"></div>
          <div class="dark-orb absolute -right-24 -top-28 h-96 w-96 rounded-full blur-3xl" aria-hidden="true"></div>

          <div class="relative grid gap-12 lg:grid-cols-12 lg:items-center">
            <div class="lg:col-span-7" data-reveal>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">
                Como os projetos são pensados
              </p>

              <h2 class="mt-5 max-w-3xl text-4xl font-semibold tracking-[-0.05em] sm:text-5xl lg:text-6xl">
                Visual limpo por fora.
                <span class="block text-zinc-500">Estrutura forte por dentro.</span>
              </h2>

              <p class="mt-6 max-w-2xl text-lg leading-8 text-zinc-400">
                Cada projeto começa pelo problema, não pela tecnologia.
                A interface simplifica; a arquitetura sustenta.
              </p>

              <div class="mt-8 flex flex-wrap gap-2">
                <span v-for="item in ['Design', 'Produto', 'Engenharia', 'Evolução']" :key="item"
                  class="rounded-full bg-white/[0.06] px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-400 ring-1 ring-white/10">
                  {{ item }}
                </span>
              </div>
            </div>

            <div class="grid gap-3 lg:col-span-5">
              <article v-for="principle in principles" :key="principle.title"
                class="rounded-[1.5rem] bg-white/[0.055] p-5 ring-1 ring-white/10 backdrop-blur-xl transition duration-500 hover:translate-x-1 hover:bg-white/[0.085]"
                data-reveal>
                <div class="flex gap-4">
                  <span class="text-xs font-bold text-zinc-600">
                    {{ principle.kicker }}
                  </span>

                  <div>
                    <h3 class="text-lg font-semibold">
                      {{ principle.title }}
                    </h3>

                    <p class="mt-2 text-sm leading-6 text-zinc-400">
                      {{ principle.desc }}
                    </p>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="relative z-10 pb-24 pt-16 sm:pb-28 sm:pt-20">
      <div class="mx-auto max-w-6xl px-5 sm:px-6 lg:px-8">
        <div
          class="cta-panel relative overflow-hidden rounded-[2.6rem] border border-[var(--border)] px-6 py-12 shadow-[var(--shadow-large)] backdrop-blur-2xl sm:px-9 sm:py-14 lg:px-12"
          data-reveal>
          <div class="cta-orb absolute -right-24 -top-24 h-72 w-72 rounded-full blur-3xl" aria-hidden="true"></div>

          <div class="relative grid gap-10 lg:grid-cols-12 lg:items-center">
            <div class="lg:col-span-8">
              <p class="eyebrow">Próximo projeto</p>

              <h2
                class="mt-4 max-w-3xl text-4xl font-semibold tracking-[-0.05em] text-[var(--text-strong)] sm:text-5xl">
                Quer construir algo nessa linha para sua operação?
              </h2>

              <p class="mt-5 max-w-2xl text-lg leading-8 text-[var(--muted)]">
                Envie o contexto, objetivo e integrações necessárias.
                A partir disso, mapeamos uma solução clara e viável.
              </p>
            </div>

            <div class="flex flex-col gap-3 lg:col-span-4">
              <a :href="whatsHref" target="_blank" rel="noopener"
                class="primary-button inline-flex h-12 items-center justify-center gap-2 rounded-full px-7 text-sm font-semibold">
                Falar no WhatsApp
                <span aria-hidden="true">→</span>
              </a>

              <router-link to="/templates"
                class="secondary-button inline-flex h-12 items-center justify-center rounded-full px-7 text-sm font-semibold">
                Ver templates
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref
} from 'vue'

import project1 from '@/assets/projects/1.png'
import project2 from '@/assets/projects/2.png'
import project3 from '@/assets/projects/3.png'
import project4 from '@/assets/projects/4.png'
import project5 from '@/assets/projects/5.png'
import project6 from '@/assets/projects/6.png'
import project7 from '@/assets/projects/7.png'
import project8 from '@/assets/projects/8.png'
import project9 from '@/assets/projects/9.png'
import project10 from '@/assets/projects/10.png'
import project11 from '@/assets/projects/11.png'
import project12 from '@/assets/projects/12.png'
import project13 from '@/assets/projects/13.png'
import project14 from '@/assets/projects/14.png'
import project15 from '@/assets/projects/15.png'
import project16 from '@/assets/projects/16.png'
import project17 from '@/assets/projects/17.png'
import project18 from '@/assets/projects/18.png'
import project19 from '@/assets/projects/19.png'
import project20 from '@/assets/projects/20.png'
import project21 from '@/assets/projects/21.png'
import project22 from '@/assets/projects/22.png'
import project23 from '@/assets/projects/23.png'
import project24 from '@/assets/projects/24.png'
import project25 from '@/assets/projects/25.png'
import project26 from '@/assets/projects/26.png'
import project27 from '@/assets/projects/27.png'
import project28 from '@/assets/projects/28.png'

const whatsHref =
  'https://wa.me/77999631644?text=' +
  encodeURIComponent(
    'Olá! Vi os projetos da Grivaro Studio e quero conversar sobre uma solução para minha operação.'
  )

const q = ref('')
const activeCategory = ref('all')
const showScrollTop = ref(false)
const pointer = reactive({ x: 50, y: 20 })

let observer = null

const techBadges = [
  'Vue',
  'Nuxt',
  'Tailwind',
  'NestJS',
  'Prisma',
  'Postgres',
  'Docker',
  'Java',
  'Spring',
  'Python',
  'Electron',
  'CI/CD'
]

const metrics = [
  { value: '20+', label: 'projetos e módulos' },
  { value: '6', label: 'frentes principais' },
  { value: '100%', label: 'foco em produto' }
]

const categories = [
  { value: 'all', label: 'Todos' },
  { value: 'systems', label: 'Sistemas' },
  { value: 'finance', label: 'Finanças' },
  { value: 'ai', label: 'IA' },
  { value: 'integrations', label: 'Integrações' },
  { value: 'web', label: 'Sites' },
  { value: 'apps', label: 'Apps' }
]

const principles = [
  {
    kicker: '01',
    title: 'Interface objetiva',
    desc: 'O visual precisa orientar a ação, não competir com ela.'
  },
  {
    kicker: '02',
    title: 'Arquitetura escalável',
    desc: 'A base técnica nasce preparada para novos módulos, integrações e regras.'
  },
  {
    kicker: '03',
    title: 'Entrega com contexto',
    desc: 'Cada decisão parte do problema, da operação e do impacto esperado.'
  }
]

const projects = [
  {
    id: 1,
    category: 'systems',
    categoryLabel: 'Sistema público',
    status: 'Online',
    title: 'VodeBuzu',
    description: 'Sistema de mobilidade com linhas em tempo real no mapa, desenvolvido para operação pública e acompanhamento de transporte.',
    image: project1,
    url: 'https://vodebuzu.pmvc.ba.gov.br/',
    tags: ['Vue', 'Quasar', 'NestJS', 'Postgres']
  },
  {
    id: 2,
    category: 'finance',
    categoryLabel: 'Finanças',
    status: 'Online',
    title: 'ControlEase Web',
    description: 'Sistema de gestão financeira para organização de movimentações, visão gerencial e controle operacional.',
    image: project2,
    url: 'https://controlease.com.br',
    tags: ['Vue', 'Tailwind', 'Spring Boot', 'Postgres']
  },
  {
    id: 3,
    category: 'web',
    categoryLabel: 'Studio',
    status: 'Online',
    title: 'NexGridCo',
    description: 'Studio digital para paletas, recursos visuais e ferramentas criativas com experiência rápida e moderna.',
    image: project3,
    url: 'https://nexgridco.com',
    tags: ['Vue', 'Tailwind', 'NestJS', 'Prisma']
  },
  {
    id: 27,
    category: 'ai',
    categoryLabel: 'IA',
    status: 'Online',
    title: 'EASE',
    description: 'Assistente pessoal inteligente com IA generativa, interação por voz, processamento de comandos e interface conversacional em tempo real.',
    image: project27,
    tags: ['AI', 'Python', 'Voice', 'Automation', 'OpenAI API']
  },
  {
    id:28,
    category: 'web',
    categoryLabel: 'Site',
    status: 'Online',
    title: 'CRM Ease',
    description: 'CRM Ease, com foco em soluções de gestão e automação de processos empresariais.',
    image: project28,
    tags: ['React', 'NestJS', 'Postgres', 'API Integration']
  }, 
  {
    id: 26,
    category: 'Site',
    categoryLabel: 'Web',
    status: 'Privado',
    title: 'UploadEase',
    description: 'Sistema de upload de arquivos com interface web, desenvolvido para operação interna e controle de documentos.',
    image: project26,
    url: '',
    tags: ['Vue.js', 'Tailwind', 'NestJS', 'Postgres']
  },
  {
    id: 4,
    category: 'web',
    categoryLabel: 'Integração',
    status: 'Online',
    title: 'Whatsapp Meta',
    description: 'Integração de WhatsApp com Meta para envio de mensagens, notificações e alertas via API oficial.',
    image: project4,
    url: '',
    tags: ['Integração', 'API', 'WhatsApp', 'Meta']
  },
  {
    id: 5,
    category: 'apps',
    categoryLabel: 'Desktop',
    status: 'Privado',
    title: 'ScreenEasex',
    description: 'Sistema de gravação com zoom inteligente para macOS, criado com interface desktop e recursos nativos.',
    image: project5,
    url: '',
    tags: ['React', 'Electron', 'macOS']
  },
  {
    id: 6,
    category: 'web',
    categoryLabel: 'Site',
    status: 'Privado',
    title: 'Estratégia Company',
    description: 'Site institucional para apresentação de serviços, soluções e presença digital da Estratégia Company.',
    image: project6,
    tags: ['Vue', 'Vite']
  },
  {
    id: 7,
    category: 'web',
    categoryLabel: 'Imobiliária',
    status: 'Online',
    title: 'Álvaro Motta Imóveis',
    description: 'Site imobiliário com apresentação de imóveis, estrutura institucional e experiência responsiva.',
    image: project7,
    url: '',
    tags: ['Vue', 'Tailwind', 'NestJS', 'Postgres']
  },
  {
    id: 8,
    category: 'ai',
    categoryLabel: 'IA & Operação',
    status: 'Online',
    title: 'Agente ControlEase',
    description: 'Agente de IA para automatizar processos financeiros e apoiar fluxos internos do ControlEase.',
    image: project8,
    url: 'https://controlease.com.br',
    tags: ['Vue', 'Tailwind', 'NestJS', 'IA']
  },
  {
    id: 9,
    category: 'systems',
    categoryLabel: 'Ferramenta',
    status: 'Privado',
    title: 'drFonts',
    description: 'Studio de gerenciamento de fontes com organização, catálogo e estrutura para uso criativo.',
    image: project9,
    url: '',
    tags: ['Vue', 'Tailwind', 'NestJS', 'Postgres']
  },
  {
    id: 10,
    category: 'web',
    categoryLabel: 'Portfólio',
    status: 'Privado',
    title: 'Gabimotta.dev',
    description: 'Portfólio pessoal desenvolvido com foco em apresentação profissional e navegação simples.',
    image: project10,
    url: '',
    tags: ['Vue', 'Nuxt', 'Vuetify']
  },
  {
    id: 11,
    category: 'ai',
    categoryLabel: 'IA',
    status: 'Privado',
    title: 'AgenteEase',
    description: 'Agente de IA para automações, atendimento e processos internos com interface web.',
    image: project11,
    url: '',
    tags: ['Vue', 'Tailwind', 'NestJS', 'IA']
  },
  {
    id: 12,
    category: 'integrations',
    categoryLabel: 'Integração',
    status: 'Privado',
    title: 'Twilio e WhatsApp',
    description: 'Integração para alertas, notificações e automações via WhatsApp conectadas ao ControlEase.',
    image: project12,
    url: '',
    tags: ['NestJS', 'Prisma', 'Twilio', 'WhatsApp']
  },
  {
    id: 13,
    category: 'finance',
    categoryLabel: 'Desktop',
    status: 'Privado',
    title: 'ControlEase Desktop',
    description: 'Sistema desktop de gestão financeira desenvolvido para controle, cadastros e acompanhamento interno.',
    image: project13,
    url: '',
    tags: ['JavaFX', 'Hibernate', 'Postgres', 'Maven']
  },
  {
    id: 14,
    category: 'integrations',
    categoryLabel: 'Integração',
    status: 'Privado',
    title: 'Integração com iFood',
    description: 'Automação de pedidos do iFood para operação da Overx, conectando dados e rotinas internas.',
    image: project14,
    url: '',
    tags: ['NestJS', 'Prisma', 'iFood', 'API']
  },
  {
    id: 15,
    category: 'web',
    categoryLabel: 'Site',
    status: 'Privado',
    title: 'Site Overx Brasil',
    description: 'Site institucional para apresentação da marca, serviços e presença digital da Overx Brasil.',
    image: project15,
    url: '',
    tags: ['Vue', 'Tailwind']
  },
  {
    id: 16,
    category: 'integrations',
    categoryLabel: 'Integração',
    status: 'Privado',
    title: 'Twilio WhatsApp Overx',
    description: 'Integração via WhatsApp para notificação de pedidos e comunicação automatizada com clientes.',
    image: project16,
    url: '',
    tags: ['NestJS', 'Prisma', 'Twilio', 'WhatsApp']
  },
  {
    id: 17,
    category: 'systems',
    categoryLabel: 'PDV',
    status: 'Privado',
    title: 'OverxPDV',
    description: 'Sistema de gerenciamento de pedidos para operação comercial com frontend, backend e banco de dados.',
    image: project17,
    url: '',
    tags: ['Vue', 'Tailwind', 'NestJS', 'Postgres']
  },
  {
    id: 18,
    category: 'ai',
    categoryLabel: 'Chatbot',
    status: 'Privado',
    title: 'ChatEase',
    description: 'Chatbot para automação de WhatsApp com backend em NestJS e frontend em Vue.',
    image: project18,
    url: '',
    tags: ['NestJS', 'Vue', 'WhatsApp']
  },
  {
    id: 19,
    category: 'web',
    categoryLabel: 'Portfólio',
    status: 'Privado',
    title: 'Portfólio 2',
    description: 'Portfólio pessoal desenvolvido com Nuxt e Tailwind, focado em presença digital simples e responsiva.',
    image: project19,
    url: '',
    tags: ['Nuxt', 'Tailwind']
  },
  {
    id: 20,
    category: 'web',
    categoryLabel: 'Portfólio',
    status: 'Privado',
    title: 'Portfólio 1',
    description: 'Primeira versão de portfólio pessoal com estrutura simples, visual direto e páginas institucionais.',
    image: project20,
    url: '',
    tags: ['Bootstrap', 'HTML', 'CSS']
  },
  {
    id: 21,
    category: 'web',
    categoryLabel: 'Site',
    status: 'Privado',
    title: 'ScriptEase',
    description: 'Site institucional desenvolvido para apresentação de marca, conteúdo e serviços digitais.',
    image: project21,
    url: '',
    tags: ['HTML', 'CSS', 'JavaScript']
  },
  {
    id: 22,
    category: 'web',
    categoryLabel: 'Hackathon',
    status: 'Online',
    title: 'StarCodes',
    description: 'Projeto desenvolvido para o NASA Space Apps, com foco em experiência web e apresentação interativa.',
    image: project22,
    url: 'https://starcodes.netlify.app',
    tags: ['Web', 'Hackathon', 'Netlify']
  },
  {
    id: 23,
    category: 'ai',
    categoryLabel: 'Chatbot',
    status: 'Privado',
    title: 'Chatbot em Python',
    description: 'Chatbot desenvolvido em Python como base para exploração de automação e inteligência artificial.',
    image: project23,
    url: '',
    tags: ['Python', 'IA', 'Chatbot']
  },
  {
    id: 24,
    category: 'systems',
    categoryLabel: 'Primeiro sistema',
    status: 'Privado',
    title: 'Sistema de Vendas',
    description: 'Sistema inicial de vendas desenvolvido com tecnologias web fundamentais.',
    image: project24,
    url: '',
    tags: ['HTML', 'CSS', 'JavaScript']
  },
  {
    id: 25,
    category: 'apps',
    categoryLabel: 'Apple',
    status: 'Privado',
    title: 'Calculadora em Swift',
    description: 'Calculadora desenvolvida em Swift para prática de interface e lógica em ambiente Apple.',
    image: project25,
    url: '',
    tags: ['Swift', 'Apple', 'iOS']
  }
]

const featuredProjects = computed(() => {
  const ids = [1, 2, 6]
  return ids.map(id => projects.find(project => project.id === id)).filter(Boolean)
})

function setCategory(category) {
  activeCategory.value = category
}

function clearFilters() {
  q.value = ''
  activeCategory.value = 'all'
}

function normalize(value) {
  return String(value || '').toLowerCase()
}

function matchesCategory(project) {
  return activeCategory.value === 'all' || project.category === activeCategory.value
}

function matchesSearch(project) {
  const term = q.value.trim().toLowerCase()

  if (!term) return true

  const content = [
    project.title,
    project.description,
    project.category,
    project.categoryLabel,
    project.status,
    ...(project.tags || [])
  ].join(' ')

  return normalize(content).includes(term)
}

const filteredProjects = computed(() =>
  projects.filter(project => matchesCategory(project) && matchesSearch(project))
)

function projectSpan(index) {
  const layout = [
    'xl:col-span-7',
    'xl:col-span-5 xl:mt-12',
    'xl:col-span-5 xl:-mt-2',
    'xl:col-span-7 xl:mt-8',
    'xl:col-span-6',
    'xl:col-span-6 xl:mt-10',
    'xl:col-span-7',
    'xl:col-span-5 xl:mt-8'
  ]

  return layout[index % layout.length]
}

function projectImageClass(index) {
  const variants = [
    'aspect-[16/9]',
    'aspect-[4/3]',
    'aspect-[4/3]',
    'aspect-[16/9]'
  ]

  return variants[index % variants.length]
}

function handlePointerMove(event) {
  const width = window.innerWidth || 1
  const height = window.innerHeight || 1

  pointer.x = Math.max(0, Math.min(100, (event.clientX / width) * 100))
  pointer.y = Math.max(0, Math.min(100, (event.clientY / height) * 100))
}

function handleScroll() {
  showScrollTop.value = window.scrollY > 520
}

function scrollToTop() {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  })
}

function setupReveal() {
  const elements = document.querySelectorAll('[data-reveal]')

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    elements.forEach(element => element.classList.add('is-visible'))
    return
  }

  observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible')
          observer?.unobserve(entry.target)
        }
      })
    },
    {
      threshold: 0.1,
      rootMargin: '0px 0px -45px 0px'
    }
  )

  elements.forEach(element => observer.observe(element))
}

onMounted(() => {
  setupReveal()
  handleScroll()

  window.addEventListener('scroll', handleScroll, {
    passive: true
  })
})

onBeforeUnmount(() => {
  observer?.disconnect()
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
:global(:root) {
  --page: #f5f5f7;
  --surface: rgba(255, 255, 255, 0.90);
  --card: rgba(255, 255, 255, 0.76);
  --chip: rgba(245, 245, 247, 0.92);
  --glass: rgba(255, 255, 255, 0.54);
  --glass-strong: rgba(255, 255, 255, 0.84);

  --text: #27272a;
  --text-strong: #09090b;
  --muted: #66666d;
  --muted-2: #a1a1aa;
  --inverse: #ffffff;

  --border: rgba(24, 24, 27, 0.085);
  --focus: rgba(113, 113, 122, 0.18);

  --shadow-soft: 0 12px 38px rgba(24, 24, 27, 0.07);
  --shadow-large: 0 32px 100px rgba(24, 24, 27, 0.105);

  --hero-a: #0a0a0b;
  --hero-b: #7c7f87;

  --orb-one: rgba(147, 161, 185, 0.22);
  --orb-two: rgba(203, 213, 225, 0.31);
  --orb-three: rgba(226, 232, 240, 0.28);

  --cta: rgba(255, 255, 255, 0.92);
}

:global(html[data-theme='dark']) {
  --page: #08090b;
  --surface: rgba(15, 16, 19, 0.90);
  --card: rgba(19, 20, 24, 0.76);
  --chip: rgba(26, 27, 31, 0.92);
  --glass: rgba(19, 20, 23, 0.54);
  --glass-strong: rgba(18, 19, 22, 0.84);

  --text: #e4e4e7;
  --text-strong: #fafafa;
  --muted: #a1a1aa;
  --muted-2: #5f6169;
  --inverse: #09090b;

  --border: rgba(255, 255, 255, 0.085);
  --focus: rgba(212, 212, 216, 0.13);

  --shadow-soft: 0 12px 42px rgba(0, 0, 0, 0.30);
  --shadow-large: 0 36px 120px rgba(0, 0, 0, 0.50);

  --hero-a: #ffffff;
  --hero-b: #777a84;

  --orb-one: rgba(71, 85, 105, 0.23);
  --orb-two: rgba(63, 63, 70, 0.28);
  --orb-three: rgba(39, 39, 42, 0.28);

  --cta: rgba(18, 19, 22, 0.94);
}

:global(html) {
  background: var(--page);
  transition: background-color 350ms ease;
}

.site-page {
  transition:
    background-color 350ms ease,
    color 350ms ease;
}

.ambient-grid {
  opacity: 0.31;
  background-image:
    linear-gradient(to right, var(--border) 1px, transparent 1px),
    linear-gradient(to bottom, var(--border) 1px, transparent 1px);
  background-size: 64px 64px;
  mask-image: linear-gradient(to bottom, black, transparent 82%);
}

.ambient-noise {
  opacity: 0.021;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.85'/%3E%3C/svg%3E");
}

.pointer-glow {
  background:
    radial-gradient(42rem circle at var(--pointer-x) var(--pointer-y),
      var(--orb-one),
      transparent 68%);
  transition: background 120ms linear;
}

.ambient-orb {
  position: absolute;
  border-radius: 9999px;
  filter: blur(95px);
  will-change: transform;
}

.ambient-orb-one {
  left: -11rem;
  top: 8rem;
  width: 34rem;
  height: 34rem;
  background: var(--orb-one);
  animation: float-a 14s ease-in-out infinite;
}

.ambient-orb-two {
  right: -12rem;
  top: 48rem;
  width: 38rem;
  height: 38rem;
  background: var(--orb-two);
  animation: float-b 18s ease-in-out infinite;
}

.ambient-orb-three {
  left: 26%;
  top: 140rem;
  width: 30rem;
  height: 30rem;
  background: var(--orb-three);
  animation: float-c 21s ease-in-out infinite;
}

.hero-gradient {
  background: linear-gradient(120deg, var(--hero-a) 5%, var(--hero-b) 96%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.primary-button {
  color: var(--inverse);
  background: var(--text-strong);
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.10),
    inset 0 1px 0 rgba(255, 255, 255, 0.12);
  transition:
    transform 300ms ease,
    box-shadow 300ms ease;
}

.primary-button:hover {
  transform: translateY(-2px);
  box-shadow:
    0 15px 32px rgba(0, 0, 0, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.13);
}

.secondary-button {
  color: var(--text-strong);
  background: var(--glass-strong);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-soft);
  backdrop-filter: blur(18px);
  transition:
    transform 300ms ease,
    border-color 300ms ease;
}

.secondary-button:hover {
  transform: translateY(-2px);
  border-color: color-mix(in srgb, var(--text-strong) 18%, transparent);
}

.hero-halo,
.cta-orb {
  background: var(--orb-one);
}

.device-frame {
  background:
    linear-gradient(135deg,
      color-mix(in srgb, var(--text-strong) 16%, transparent),
      color-mix(in srgb, var(--text-strong) 4%, transparent));
  border: 1px solid var(--border);
  box-shadow:
    var(--shadow-large),
    inset 0 1px 0 rgba(255, 255, 255, 0.10);
  backdrop-filter: blur(18px);
}

.featured-panel {
  background: #111215;
  border: 1px solid rgba(255, 255, 255, 0.07);
}

.featured-overlay {
  background:
    linear-gradient(to top, rgba(0, 0, 0, .92), rgba(0, 0, 0, .20) 60%, rgba(0, 0, 0, .08)),
    linear-gradient(to right, rgba(0, 0, 0, .28), transparent 60%);
}

.dashboard-card {
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.075);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.025);
  transition:
    background-color 300ms ease,
    transform 300ms ease;
}

.dashboard-card:hover {
  background: rgba(255, 255, 255, 0.065);
  transform: translateY(-2px);
}

.portfolio-dock {
  transition:
    transform 400ms ease,
    box-shadow 400ms ease;
}

.portfolio-dock:focus-within {
  transform: translateY(-2px);
  box-shadow: var(--shadow-large);
}

.tech-rail {
  mask-image:
    linear-gradient(to right,
      transparent,
      black 7%,
      black 93%,
      transparent);
}

.tech-track {
  animation: marquee 32s linear infinite;
}

.tech-rail:hover .tech-track {
  animation-play-state: paused;
}

.flow-line {
  position: absolute;
  left: 50%;
  top: 0;
  width: 1px;
  height: 100%;
  background:
    linear-gradient(to bottom,
      transparent,
      var(--border) 25%,
      var(--border) 75%,
      transparent);
}

.flow-node {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 9px;
  height: 9px;
  border: 2px solid var(--page);
  border-radius: 9999px;
  background: var(--text-strong);
  box-shadow:
    0 0 0 5px color-mix(in srgb, var(--text-strong) 8%, transparent);
  transform: translate(-50%, -50%);
}

.eyebrow {
  font-size: 0.72rem;
  line-height: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--muted);
}

.section-title {
  max-width: 52rem;
  font-size: clamp(2.35rem, 4.7vw, 4rem);
  line-height: 1.02;
  font-weight: 600;
  letter-spacing: -0.055em;
  color: var(--text-strong);
}

.project-card {
  backdrop-filter: blur(18px);
}

.project-card:hover {
  transform: translateY(-6px);
  border-color: color-mix(in srgb, var(--text-strong) 14%, transparent);
  box-shadow: var(--shadow-soft);
}

.project-overlay {
  background:
    linear-gradient(to top, rgba(0, 0, 0, .16), transparent 45%);
}

.curve-transition {
  position: relative;
  width: 100%;
  height: 5.5rem;
  overflow: hidden;
}

.curve-transition::before {
  content: '';
  position: absolute;
  left: 12%;
  top: 50%;
  width: 76%;
  height: 5rem;
  border-top: 1px solid var(--border);
  border-radius: 50%;
  transform: translateY(18%);
}

.curve-dot {
  position: absolute;
  left: 50%;
  top: 2.25rem;
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: var(--text-strong);
  transform: translateX(-50%);
}

.dark-panel {
  background:
    radial-gradient(circle at 85% 20%, rgba(255, 255, 255, .07), transparent 30%),
    #090a0c;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, .08),
    0 36px 110px rgba(0, 0, 0, .22);
}

.dark-panel-grid {
  opacity: 0.31;
  background-image:
    linear-gradient(to right, rgba(255, 255, 255, .055) 1px, transparent 1px),
    linear-gradient(to bottom, rgba(255, 255, 255, .055) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: linear-gradient(to left, black, transparent 74%);
}

.dark-orb {
  background: rgba(255, 255, 255, .07);
}

.cta-panel {
  background:
    linear-gradient(135deg,
      var(--cta),
      color-mix(in srgb, var(--cta) 85%, var(--page)));
}

[data-reveal] {
  opacity: 0;
  transform: translateY(26px) scale(.992);
  transition:
    opacity 780ms cubic-bezier(.22, .82, .22, 1),
    transform 780ms cubic-bezier(.22, .82, .22, 1);
}

[data-reveal].is-visible {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.catalog-enter-active,
.catalog-leave-active,
.catalog-move {
  transition:
    opacity 350ms ease,
    transform 350ms ease;
}

.catalog-enter-from,
.catalog-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(.98);
}

.scroll-top-enter-active,
.scroll-top-leave-active {
  transition:
    opacity 0.25s ease,
    transform 0.25s ease;
}

.scroll-top-enter-from,
.scroll-top-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.92);
}

@keyframes marquee {
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(calc(-50% - 0.25rem));
  }
}

@keyframes float-a {

  0%,
  100% {
    transform: translate3d(0, 0, 0);
  }

  50% {
    transform: translate3d(3rem, 2rem, 0);
  }
}

@keyframes float-b {

  0%,
  100% {
    transform: translate3d(0, 0, 0);
  }

  50% {
    transform: translate3d(-3rem, -2rem, 0);
  }
}

@keyframes float-c {

  0%,
  100% {
    transform: translate3d(0, 0, 0);
  }

  50% {
    transform: translate3d(1rem, -3rem, 0);
  }
}

@media (max-width: 640px) {
  .ambient-grid {
    background-size: 44px 44px;
  }

  .curve-transition::before {
    left: 2%;
    width: 96%;
  }
}

@media (prefers-reduced-motion: reduce) {

  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }

  [data-reveal] {
    opacity: 1;
    transform: none;
  }
}
</style>
